import React, { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { actionUserLogout } from "../Redux/UserReducer/actionType";
import { useToast } from "@chakra-ui/react";

// 20 Minutes total timeout
const TIMEOUT_MS = 20 * 60 * 1000;

export const SessionTimeout = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const userStore = useSelector((store) => store.UserReducer);

  // Use ref to prevent constant re-renders
  const logoutTimerRef = useRef(null);

  const localUser = JSON.parse(localStorage.getItem("user")) || {};
  const isAuth = userStore?.isAuth || localUser?.isAuth;

  const toast = useToast();

  const handleLogout = useCallback(async () => {
    // Only attempt to call backend if we have a token
    const token = localUser?.token || userStore?.token;
    if (token) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.log("Auto-logout request failed", err);
      }
    }

    // Clear local storage completely
    localStorage.removeItem("user");
    sessionStorage.clear();
    
    // Dispatch to Redux
    dispatch(actionUserLogout());
    
    toast({
      title: "Session Expired",
      description: "You have been automatically logged out due to inactivity. Please log in again.",
      status: "warning",
      duration: 6000,
      isClosable: true,
      position: "top"
    });

    // Send them to the Not Allowed page which will redirect to public landing page
    if (location.pathname !== "/not-allowed" && location.pathname !== "/") {
      navigate("/not-allowed");
    }
  }, [dispatch, navigate, localUser?.token, userStore?.token, location.pathname, toast]);

  const resetTimers = useCallback(() => {
    clearTimeout(logoutTimerRef.current);

    if (isAuth) {
      // Set actual forced logout timer
      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, TIMEOUT_MS);
    }
  }, [isAuth, handleLogout]);

  useEffect(() => {
    // Bind all activity events
    const events = ["mousemove", "keydown", "wheel", "DOMMouseScroll", "mouseWheel", "mousedown", "touchstart", "touchmove"];
    
    const handleActivity = () => {
      resetTimers();
    };

    if (isAuth) {
      events.forEach((event) => {
        window.addEventListener(event, handleActivity, { passive: true });
      });
      // Initial set
      resetTimers();
    }

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimeout(logoutTimerRef.current);
    };
  }, [isAuth, resetTimers]);

  return <>{children}</>;
};
